import { Asset, DCFInput, OtherCF, RentRoll } from "reia-dcf-client";
import moment from "moment";

import {
    AssetDto,
    CalculationDetailDto, DCFParameters,
    DCFResult,
    OtherIncomeCost,
    RentRollAssumptions,
    RentRollDto
} from "reia-rest-client";
import { getFormattedInt, getFormattedEnglishNumber } from "./digitHelper";
import { DCFOutput } from "reia-dcf-client";
import {
    DCF_FINANCING_AMORTIZATION,
    DCF_FINANCING_INTEREST_RATE,
    DCF_FINANCING_LTV, DCF_FINANCING_PREPAYMENT_FEE,
    DCF_FINANCING_SWAP_RATE, DCF_FINANCING_TARGET_VALUE, DCF_FINANCING_TYPE_I18N_KEY
} from "./dcfDefaults";
import { Moment } from "moment";
import dayjs from "dayjs";


export const getDCFInput = (assetId: string, analysisDate: string, assetDCFParams: any, assetHVLParams: any, rentRolls: RentRollDto[], rentRollAssumptions: RentRollAssumptions, indicesValues: Object, dcfResult: DCFOutput): DCFInput => {
    const validatedAnalysisDate: Moment = getAnalysisDate(analysisDate)
    const dcfAsset: Asset = getDCFAsset(assetId, validatedAnalysisDate, assetDCFParams, dcfResult);
    let dcfRentRolls: RentRoll[];
    if (rentRolls?.length > 0) {
        dcfRentRolls = rentRolls.map(rentRoll => {
            let rentRollItem = rentRoll;
            if (rentRollAssumptions.hasOwnProperty(rentRoll.useType.id)) {
                rentRollItem = overrideAssumptionValues(rentRoll, rentRollAssumptions[rentRoll.useType.id])
            }
            const cpiValue = indicesValues ? indicesValues[getCpiType(rentRollItem.cpiType?.key)] : undefined
            return getDCFRentRoll(rentRollItem, cpiValue)
        })
    } else {
        dcfRentRolls = getHVLDCFRentRolls(validatedAnalysisDate, assetHVLParams)
    }
    const otherCFs: OtherCF[] = getOtherDCFs(assetDCFParams);

    return { dcf_id: "ignore", asset: dcfAsset, rentRolls: dcfRentRolls, otherCFs: otherCFs }
}

const overrideAssumptionValues = (rentRoll: RentRollDto, rentRollAssumption: any) => {
    const updatedRentRoll: RentRollDto = JSON.parse(JSON.stringify(rentRoll));
    Object.keys(rentRollAssumption).forEach(paraName => {
        const rentRollParamName = paraName.replace("Avg", "");
        updatedRentRoll[rentRollParamName] = rentRollAssumption[paraName];
        if (paraName === "marketRentPerSqmAvg") {
            updatedRentRoll.marketRentPerMonth = updatedRentRoll.marketRentPerSqm * updatedRentRoll.rentalSpace
        }
    })
    return updatedRentRoll;
}

const getDCFAsset = (assetId: string, validatedAnalysisDate: Moment, assetDCFParams: DCFParameters, dcfResult: DCFOutput): Asset => {
    const dcfAsset: Asset = {
        "assetId": "" + assetId,
        "analysisDate": validatedAnalysisDate.format("YYYY-MM-DD"),
        "durationInMonths": assetDCFParams.durationInMonths,
        "inflation": assetDCFParams.inflation.length > 0 ? assetDCFParams.inflation.map(value => typeof value === 'number' ? getMonthlyPercentageAsDecimal(value) : value) : [],
        "ervGrowth": assetDCFParams.ervGrowth.length > 0 ? assetDCFParams.ervGrowth.map(value => typeof value === 'number' ? getMonthlyPercentageAsDecimal(value) : value) : [],
        "ervGrowthType": assetDCFParams.syncWithInflation ? 1 : 2,
        "discountRate": getMonthlyPercentageAsDecimal(assetDCFParams.discountRate),
        "capRate": (assetDCFParams.exitCapRate / 100) / 12,
        "baseRent": getBaseRentTypeFromI18nKey(assetDCFParams.baseRentType?.key),
        "terminalValueType": getTerminalRentTypeFromI18nKey(assetDCFParams.terminalValueType?.key),
        "landTransferTax": assetDCFParams.landTransferTax / 100,
        "agentCosts_Sale": assetDCFParams.agentCosts_Sale / 100,
        "notaryCosts": assetDCFParams.notaryCosts / 100,
        "landTransferTaxExit": assetDCFParams.landTransferTaxExit / 100,
        "agentCostsExit": assetDCFParams.agentCostsExit / 100,
        "notaryCostsExit": assetDCFParams.notaryCostsExit / 100
    }

    if (assetDCFParams?.triggerFinancingCosts) {
        dcfAsset.financing_Type = getFinancingType(assetDCFParams?.financingType?.key);
        dcfAsset.financing_LTV = getFinancingLTV(assetDCFParams, dcfResult) / 100;
        dcfAsset.financing_Loan = assetDCFParams?.financingLoan ? assetDCFParams?.financingLoan : undefined;
        dcfAsset.financing_Equity = assetDCFParams?.financingEquity ? assetDCFParams?.financingEquity : undefined;
        dcfAsset.financing_Armortization = dcfAsset.financing_Type !== 1 ? 0 : assetDCFParams.financingAmortization ? assetDCFParams.financingAmortization / 12 : DCF_FINANCING_AMORTIZATION / 12;
        dcfAsset.financing_TargetValue = dcfAsset.financing_Type !== 2 ? 0 : assetDCFParams.financingTargetValue ? assetDCFParams.financingTargetValue : DCF_FINANCING_TARGET_VALUE;
        dcfAsset.financing_PrepaymentFee = assetDCFParams.financingPrepaymentFee ? assetDCFParams.financingPrepaymentFee : DCF_FINANCING_PREPAYMENT_FEE;
        dcfAsset.financing_AllIntestRate = getFinancingAllInterestRate(assetDCFParams, dcfResult)
    }

    return dcfAsset;
}

const getMonthlyPercentageAsDecimal = (percent: number) => {
    return Math.pow(1 + (percent / 100), 1 / 12) - 1
}

const getMonthlyPercentageAsPercentage = (percent: number) => {
    return getMonthlyPercentageAsDecimal(percent) * 100
}

export const compareAssetValue = (calculationDetail: CalculationDetailDto, assetDCFResult: DCFResult) => {

    return Math.round(calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue) === Math.round(assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue)
}


export const deepObjectAsStringCompare = (object1, object2) => {
    return JSON.stringify(object1) === JSON.stringify(object2); // true
}

const getDCFRentRoll: RentRoll = (rentRollDto: RentRollDto, cpiSelected: number): RentRoll => {

    const rentRoll: RentRoll = {
        "rentRollId": "" + rentRollDto.id,
        "status": rentRollDto.rentRollStatusType ? getRentRollStatusNumber(rentRollDto.rentRollStatusType.key) : undefined,
        "use": rentRollDto.useType.key,
        "areaUnit": rentRollDto.rentalSpace ? rentRollDto.rentalSpace : 0.00000000000001,
        "currentRent": rentRollDto.currentRentPerMonth && rentRollDto.currentRentPerMonth > 0 ? rentRollDto.currentRentPerMonth : 0.00000000000001,
        "leaseStart": moment(rentRollDto?.leaseStartDate).format("YYYY-MM-DD"),
        "leaseEnd": moment(rentRollDto?.leaseEndDate).format("YYYY-MM-DD"),
        "option1": rentRollDto?.option1Date ? moment(rentRollDto?.option1Date).format("YYYY-MM-DD") : undefined,
        "option2": rentRollDto?.option2Date ? moment(rentRollDto?.option2Date).format("YYYY-MM-DD") : undefined,
        "initialRenewalProbability": rentRollDto.initialRenewalProbability / 100,
        "continuingRenewalProbability": rentRollDto.continuingRenewalProbability / 100,
        "leaseDurationOpenEnd": rentRollDto.leaseDurationOpenEndInMonths,
        "leaseDurationNewLeases": rentRollDto.leaseDurationInMonths,
        "marketRent": rentRollDto.marketRentPerMonth ? rentRollDto.marketRentPerMonth : undefined,
        "maintenance_Term": rentRollDto.nrctMaintenance,
        "management_Term": rentRollDto.nrctManagement / 100,
        "otherCosts_Term": rentRollDto.nrctOtherCosts / 100,
        "maintenance_Reletting": rentRollDto.nrcrMaintenance,
        "management_Reletting": rentRollDto.nrcrManagement / 100,
        "otherCosts_Reletting": rentRollDto.nrcrOtherCosts / 100,
        "voidPeriodInitial": rentRollDto.initialVoidPeriod,
        "voidPeriodContinuing": rentRollDto.continuingVoidPeriod,
        "vacancyCosts": rentRollDto.vacancyCosts,
        "initialTenantImprovements": rentRollDto.relettingCostsInitialTIs,
        "continuingTenantImprovements": rentRollDto.relettingCostsContinuingTIs,
        "agentCosts_Reletting": rentRollDto.relettingCostsAgent,
        "indexationType": getIndexationTypeNumber(rentRollDto.indexationType?.key),
        "cpiSelected": cpiSelected ? cpiSelected : undefined,
        //"baseIndex": 0,
        "baseIndexValue": rentRollDto.indexationValue && rentRollDto.indexationValue > 0 ? rentRollDto.indexationValue : 100, //TODO: Do not expect a default value in dcf
        //"baseIndexDate": moment(rentRollDto.indexationLastDate).format("YYYY-MM-DD"),
        "nextAdjustment": rentRollDto.indexationNextDate ? moment(rentRollDto.indexationNextDate).format("YYYY-MM-DD") : undefined,
        "indexationCycle": rentRollDto.indexationCycleInMonths ? rentRollDto.indexationCycleInMonths : undefined,
        "indexationHurdle": rentRollDto?.indexationHurdle ? rentRollDto.indexationHurdle : undefined,
        "indexationAdjustmentFactor": rentRollDto.indexationAdjustmentFactor / 100,
        "nextAdjustmmentCPI": rentRollDto.nextIndexValue && rentRollDto.nextIndexValue > 0 ? rentRollDto.nextIndexValue : 100, //TODO: Do not expect a default value in dcf
    }

    if (rentRollDto?.individualIndexations) {
        Object.entries(rentRollDto?.individualIndexations).forEach((entry, index) => {
            rentRoll["individualIndexationDate" + (index + 1)] = moment(entry[0]).format("YYYY-MM-DD")
            rentRoll["individualIndexationRent" + (index + 1)] = entry[1]
        });
    }

    return rentRoll
}

const getHVLDCFRentRolls = (validatedAnalysisDate: Moment, assetHVLParams) => {
    const rentRolls = [];
    const leaseEnd = validatedAnalysisDate.clone().set('year', validatedAnalysisDate.year() + assetHVLParams.leaseDuration)
    const option1 = leaseEnd.clone().set('year', leaseEnd.year() + assetHVLParams.option1InYears)


    if (assetHVLParams.occupiedArea > 0) {

        const letRentRoll: RentRoll = {
            "rentRollId": "rentroll-let",
            "status": 1,
            "use": "string",
            "areaUnit": assetHVLParams.totalLettableArea * assetHVLParams.occupiedArea / 100,
            "currentRent": assetHVLParams.currentRentMonthly,
            "leaseStart": validatedAnalysisDate.format("YYYY-MM-DD"),
            "leaseEnd": leaseEnd.format("YYYY-MM-DD"),
            "option1": option1.format("YYYY-MM-DD"),
            //"option2": null,
            "initialRenewalProbability": assetHVLParams.renewalProbability / 100,
            "continuingRenewalProbability": assetHVLParams.renewalProbability / 100,
            "leaseDurationOpenEnd": 36,
            "leaseDurationNewLeases": assetHVLParams.marketLease * 12,
            "marketRent": assetHVLParams.marketRentMonthly,
            "maintenance_Term": 0,
            "management_Term": 0,
            "otherCosts_Term": assetHVLParams.otherCosts / 100,
            "maintenance_Reletting": 0,
            "management_Reletting": 0,
            "otherCosts_Reletting": 0,
            "voidPeriodInitial": assetHVLParams.voidPeriodInitial,
            "voidPeriodContinuing": assetHVLParams.voidPeriodContinuing,
            "vacancyCosts": 24,
            "initialTenantImprovements": assetHVLParams.initialTenantImprovements,
            "continuingTenantImprovements": assetHVLParams.continuingTenantImprovements,
            "agentCosts_Reletting": 3,
            "cpiSelected": 119.2829085,
            "indexationType": assetHVLParams.indexationType && assetHVLParams.indexationType === "Ja" ? 3 : 1, //wenn yes dann 3
            //"baseIndex": 0,
            "baseIndexValue": 111.5, // TODO: Klären ob für Typ 3 oder 1 notwendig
            //"baseIndexDate": asset?.analysisDate,
            "nextAdjustment": validatedAnalysisDate.set('year', validatedAnalysisDate.year() + 1).format("YYYY-MM-DD"),
            "indexationCycle": 12,
            "indexationHurdle": 5,
            "indexationAdjustmentFactor": assetHVLParams.indexationAdjustmentFactor / 100,
            "nextAdjustmmentCPI": 119.2829085, //TODO: Klären
            "individualIndexationDate1": "2024-01-01",
            "individualIndexationRent1": 52000,
            "individualIndexationDate2": "2025-01-01",
            "individualIndexationRent2": 54000,
            "individualIndexationDate3": "2026-01-01",
            "individualIndexationRent3": 56000,
            "individualIndexationDate4": "2027-01-01",
            "individualIndexationRent4": 58000,
            "individualIndexationDate5": "2028-01-01",
            "individualIndexationRent5": 60000
        }

        rentRolls.push(letRentRoll);
        if (assetHVLParams.debugCalc) {
            let loop = 0;
            for (loop; loop < 100; loop++) {
                let newRentRoll: RentRoll = JSON.parse(JSON.stringify(letRentRoll));
                newRentRoll.rentRollId = "rentroll-let" + (loop + 1);
                newRentRoll.currentRent = Math.floor(newRentRoll.currentRent * (1 + (Math.random() / 10)));
                rentRolls.push(newRentRoll);
            }
        }
    }

    if (assetHVLParams.occupiedArea < 100) {
        const vacantRentRoll: RentRoll = {
            "rentRollId": "rentroll-vacant",
            "status": 0,
            "use": "string",
            "areaUnit": assetHVLParams.totalLettableArea * (1 - assetHVLParams.occupiedArea / 100),
            "currentRent": 1,
            "leaseStart": validatedAnalysisDate.format("YYYY-MM-DD"), //TODO: Daten identisch zu let?
            "leaseEnd": leaseEnd.format("YYYY-MM-DD"), //TODO: Daten identisch zu let?
            "option1": option1.format("YYYY-MM-DD"), //TODO: Daten identisch zu let?
            //"option2": null,
            "initialRenewalProbability": assetHVLParams.renewalProbability / 100,
            "continuingRenewalProbability": assetHVLParams.renewalProbability / 100,
            "leaseDurationOpenEnd": 36,
            "leaseDurationNewLeases": assetHVLParams.marketLease * 12,
            "marketRent": assetHVLParams.totalLettableArea * (1 - assetHVLParams.occupiedArea / 100) * assetHVLParams.marketRentSQM,
            "maintenance_Term": 0,
            "management_Term": 0,
            "otherCosts_Term": assetHVLParams.otherCosts / 100,
            "maintenance_Reletting": 0,
            "management_Reletting": 0,
            "otherCosts_Reletting": 0,
            "voidPeriodInitial": assetHVLParams.voidPeriodInitial,
            "voidPeriodContinuing": assetHVLParams.voidPeriodContinuing,
            "vacancyCosts": 24,
            "initialTenantImprovements": assetHVLParams.initialTenantImprovements,
            "continuingTenantImprovements": assetHVLParams.continuingTenantImprovements,
            "agentCosts_Reletting": 3,
            "indexationType": assetHVLParams.indexationType && assetHVLParams.indexationType === "Ja" ? 3 : 1, //wenn yes dann 3
            "cpiSelected": 119.2829085,
            //"baseIndex": 0,
            "baseIndexValue": 111.5, // TODO: Klären ob für Typ 3 oder 1 notwendig
            //"baseIndexDate": asset?.analysisDate,
            "nextAdjustment": validatedAnalysisDate.set('year', validatedAnalysisDate.year() + 1).format("YYYY-MM-DD"),
            "indexationCycle": 12,
            "indexationHurdle": 5,
            "indexationAdjustmentFactor": assetHVLParams.indexationAdjustmentFactor / 100,
            "nextAdjustmmentCPI": 119.2829085, //TODO: Klären
            "individualIndexationDate1": "2024-01-01",
            "individualIndexationRent1": 52000,
            "individualIndexationDate2": "2025-01-01",
            "individualIndexationRent2": 54000,
            "individualIndexationDate3": "2026-01-01",
            "individualIndexationRent3": 56000,
            "individualIndexationDate4": "2027-01-01",
            "individualIndexationRent4": 58000,
            "individualIndexationDate5": "2028-01-01",
            "individualIndexationRent5": 60000
        }

        rentRolls.push(vacantRentRoll);
    }
    return rentRolls;
}

const getOtherDCFs = (assetDCFParams: any): OtherCF[] => {
    let otherCFs: OtherCF[] = [];

    if (assetDCFParams.otherIncomeCosts) {
        otherCFs = assetDCFParams.otherIncomeCosts.map((item: OtherIncomeCost) => {

            const type: number = item.type ? item.type === "OPEX" ? 1 : 2 : 0;
            let cycle: number = 0;
            let endDate = item.end;

            switch (item.rotation) {

                case "ONETIME":
                    cycle = 1;
                    endDate = item.start;
                    break;
                case "MONTHLY": cycle = 2; break;
                case "QUARTER": cycle = 3; break;
                case "HALFYEARLY": cycle = 4; break;
                case "YEARLY": cycle = 5; break;

            }

            return {
                "description": item.description,
                "amount": item.value,
                "type": type,
                "cycle": cycle,
                "startDate": item.start,
                "endDate": endDate
            };
        });
    }

    return otherCFs;
}

const getFinancingType = (key: string): number => {
    switch (key) {
        case "core-data.financingTypes.amortization-loan": return 1
        case "core-data.financingTypes.annuity-loan": return 2
        case "core-data.financingTypes.bullet-loan": return 3
        default: return 3;
    }
}

export const getFinancingTypeByParams = (assetDCFParams: DCFParameters) => {
    return getFinancingType(assetDCFParams.financingType
        ? assetDCFParams.financingType.key
        : DCF_FINANCING_TYPE_I18N_KEY)
}

const getCpiType = (key: string): String => {
    switch (key) {
        case "core-data.cpiTypes.vpi-2015": return "cpi2015"
        case "core-data.cpiTypes.vpi-2020": return "cpi2020"
        case "core-data.cpiTypes.vpi-2025": return "cpi2025"

        default: return null;
    }
}

const getIndexationTypeNumber = (key: string): number => {
    switch (key) {
        case "core-data.indexationTypes.none": return 1
        case "core-data.indexationTypes.fixed": return 2
        case "core-data.indexationTypes.cpi-period": return 3
        case "core-data.indexationTypes.cpi-hurdle-": return 4
        case "core-data.indexationTypes.cpi-hurdle-pts": return 5
        case "core-data.indexationTypes.individual": return 6

        //Default return none!
        default: return 1;
    }
}

const getRentRollStatusNumber = (key: string): number => {
    switch (key) {
        case "core-data.rentRollStatusTypes.vacant": return 0
        case "core-data.rentRollStatusTypes.let": return 1

        default: return undefined;
    }
}

export const getTerminalRentTypeFromI18nKey = (key: string) => {
    switch (key) {
        case "core-data.terminalValueTypes.gross": return 1
        case "core-data.terminalValueTypes.net": return 2

        default: return undefined;
    }
}

const getBaseRentTypeFromI18nKey = (key: string) => {
    switch (key) {
        case "core-data.baseRentTypes.potential-rent": return 2
        case "core-data.baseRentTypes.market-rent": return 1
        case "core-data.baseRentTypes.current-rent": return 3

        default: return undefined;
    }
}

export const getPerSpaceValue = (value: number, assetHVLParams: any, rentRolls: RentRollDto[]) => {
    // return getFormattedInt(value/getTotalSpace(assetHVLParams, rentRolls));
    return getFormattedEnglishNumber(value / getTotalSpace(assetHVLParams, rentRolls));
}

export const getPerSpaceValueUnformatted = (value: number, assetHVLParams: any, rentRolls: RentRollDto[]) => {
    return value / getTotalSpace(assetHVLParams, rentRolls);
}

export const getTotalSpace = (assetHVLParams: any, rentRolls: RentRollDto[]) => {

    let totalSpace;
    if (rentRolls?.length > 0) {
        totalSpace = rentRolls.reduce((summedSpace, rentRoll) => summedSpace + rentRoll.rentalSpace, 0)
    } else {
        totalSpace = assetHVLParams.totalLettableArea;
    }

    return totalSpace;
}

export const getNetGrossTerminalValue = (assetDCFParams: DCFParameters, dcfResult: DCFOutput) => {

    const { landTransferTaxExit, agentCostsExit, notaryCostsExit, terminalValueType } = assetDCFParams;
    const accCostPercentage = landTransferTaxExit + agentCostsExit + notaryCostsExit;
    const terminalType: number = terminalValueType ? getTerminalRentTypeFromI18nKey(terminalValueType.key) : 0;

    let netValue;
    let grossValue;
    if (terminalType === 1) {
        grossValue = dcfResult?.assetCashFlow?.terminalValue;
        netValue = grossValue * 100 / (100 + accCostPercentage)
    }
    else {
        netValue = dcfResult?.assetCashFlow?.terminalValue;
        grossValue = netValue * (100 + accCostPercentage) / 100
    }

    return {
        grossValue, netValue
    };
}

export const getAccCostsTerminalValue = (assetDCFParams: DCFParameters, dcfResult: DCFOutput) => {

    const { landTransferTaxExit, agentCostsExit, notaryCostsExit } = assetDCFParams;

    const { grossValue, netValue } = getNetGrossTerminalValue(assetDCFParams, dcfResult)

    const landTransferExitValue = netValue * landTransferTaxExit / 100
    const notaryCostsExitValue = netValue * notaryCostsExit / 100
    const agentCostsExitValue = netValue * agentCostsExit / 100
    const totalAcquisitionCostsExitValue = landTransferExitValue + notaryCostsExitValue + agentCostsExitValue;

    return {
        grossValue, netValue, landTransferExitValue, notaryCostsExitValue, agentCostsExitValue, totalAcquisitionCostsExitValue
    };
}

export const getFinancingAllInterestRate = (assetDCFParams: DCFParameters) => {
    let interestRate: number = assetDCFParams?.financingInterestRate ? assetDCFParams.financingInterestRate : DCF_FINANCING_INTEREST_RATE
    let swapRate: number = assetDCFParams?.financingSwapRate ? assetDCFParams.financingSwapRate : DCF_FINANCING_SWAP_RATE
    return Math.pow(1 + (parseFloat(interestRate) + parseFloat(swapRate)) / 100, 1 / 12) - 1;
}

export const getFinancingLoan = (assetDCFParams: DCFParameters, dcfResult: DCFOutput) => {
    if (assetDCFParams?.financingLoan)
        return assetDCFParams?.financingLoan;

    let percentage = getFinancingLTV(assetDCFParams, dcfResult);
    const assetValue = dcfResult?.assetCashFlow?.netAssetValue
    return percentage * assetValue;
}

export const getFinancingEquity = (assetDCFParams: DCFParameters, dcfResult: DCFOutput) => {
    if (assetDCFParams?.financingEquity)
        return assetDCFParams?.financingEquity;

    let loan;
    if (assetDCFParams?.financingLoan) {
        loan = assetDCFParams?.financingLoan;
    } else {
        loan = getFinancingLoan(assetDCFParams, dcfResult)
    }
    const grossAssetValue = dcfResult?.assetCashFlow?.grossAssetValue
    return grossAssetValue - loan;
}

export const getFinancingLTV = (assetDCFParams: DCFParameters, dcfResult: DCFOutput) => {
    //=WENN(UND(ISTLEER(LTV);ISTLEER(LOAN));0,5;WENN(LOAN<>0;LOAN/SUMME(LOAN:EQUITY);LTV))
    let ltv;
    if (!assetDCFParams?.financingLTV && !assetDCFParams?.financingLoan) {
        ltv = DCF_FINANCING_LTV;
    } else if (assetDCFParams?.financingLoan) {
        const equity = getFinancingEquity(assetDCFParams, dcfResult);
        ltv = assetDCFParams.financingLoan / (parseFloat(assetDCFParams.financingLoan) + parseFloat(equity));
    } else {
        ltv = assetDCFParams?.financingLTV / 100;
    }
    return ltv;
}

export const getAnalysisDate = (analysisDate: String): Moment => {
    if (analysisDate) {
        const analysisDateMoment = moment(analysisDate);
        return analysisDateMoment.date() > 15 ?
            analysisDateMoment.set("date", 1).set("month", analysisDateMoment.month() + 1) :
            analysisDateMoment.set("date", 1)
    } else {
        return moment(moment.now()).set("date", 1);
    }
}
// Calculating total lettable area 
export const getTotalLettableArea = (calculationDetail) => {
    const rentRollsLettableArea = calculationDetail?.rentRolls
        ? Object.values(calculationDetail?.rentRolls)?.reduce(
            (total, item) => total + item.rentalSpace,
            0
        )
        : null;
    const totalLettableArea = rentRollsLettableArea ??
        calculationDetail?.assetHVLParams?.totalLettableArea;
    return totalLettableArea
}

export const getRentalPerAnum = (calculationDetail, type) => {
    const currentRental =
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalGrossRentalIncome[0] * 12;

    const marketRental =
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalMArketRent[0] * 12;

    const potentialRental =
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalPotentialRent[0] * 12;
    if (type === 'currentRent') {
        return currentRental;
    } else if (type === 'marketRent') {
        return marketRental;
    } else {
        return potentialRental;
    }
}

export const getAssetValue = (calculation) => {
    const assetValue = calculation?.assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue
    return assetValue;
}
export const getAssetValuePSM = (calculation) => {
    const assetValue = calculation?.assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue
    const assetValuePSM = getPerSpaceValue(assetValue, calculation.assetHVLParams, Object.values(calculation.calculationDetail.rentRolls));
    return assetValuePSM;
}
export const getVacancyRate = (calculationDetail) => {
    const totalLettableArea = getTotalLettableArea(calculationDetail)
    const vacantRentalSpace =
        calculationDetail?.rentRolls &&
        Object.values(calculationDetail?.rentRolls)
            ?.filter(
                (list) =>
                    list?.rentRollStatusType?.key ===
                    "core-data.rentRollStatusTypes.vacant"
            )
            ?.reduce((total, item) => total + item.rentalSpace, 0);
    const vacancyRate = vacantRentalSpace / totalLettableArea;
    return vacancyRate;
}

export const getTerminalType = (calculationDetail) => {
    const terminalType = calculationDetail?.assetDCFParams?.terminalValueType
        ? getTerminalRentTypeFromI18nKey(
            calculationDetail?.assetDCFParams?.terminalValueType?.key
        )
        : 0
    return terminalType;
}

export const getTerminalValue = (calculationDetail, type) => {
    const terminalType = getTerminalType(calculationDetail);
    const accCostPercentage =
        calculationDetail?.assetDCFParams?.landTransferTaxExit +
        calculationDetail?.assetDCFParams?.agentCostsExit +
        calculationDetail?.assetDCFParams?.notaryCostsExit;
    let netValue;
    if (terminalType === 1) {
        const grossValue =
            calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
                ?.terminalValue;
        netValue = (grossValue * 100) / (100 + accCostPercentage);
        if (type === 'gross') {
            return grossValue;
        } else {
            return netValue
        }
    } else {
        netValue =
            calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
                ?.terminalValue;
        if (type === 'gross') {
            return (netValue * (100 + accCostPercentage)) / 100;
        } else {
            return netValue
        }
    }
}


export const getYeildProfileCalculations = (calculationDetail) => {
    const yielCurrentNIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalNetOperatingIncome[0] *
            12) /
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.grossAssetValue;

    const yielCurrentGIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalGrossRentalIncome[0] *
            12) /
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue;

    const yielPotentialNIY =
        ((calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalPotentialRent[0] -
            (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
                ?.totalNonRecs[0] +
                calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
                    ?.otherIncomeCosts_beforeNOI[0])) *
            12) /
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.grossAssetValue;

    const yielPotentialGIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalPotentialRent[0] *
            12) /
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue;

    const yielMarketNIY =
        ((calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalMArketRent[0] -
            (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
                ?.totalNonRecs[0] +
                calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
                    ?.otherIncomeCosts_beforeNOI[0])) *
            12) /
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.grossAssetValue;

    const yielMarketGIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalMArketRent[0] *
            12) /
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.netAssetValue;

    return { yielMarketGIY, yielMarketNIY, yielPotentialGIY, yielPotentialNIY, yielCurrentGIY, yielCurrentNIY }
}

export const getYeildProfileAtSaleCalculations = (calculationDetail) => {
    const grossTerminalValue = getTerminalValue(calculationDetail, 'gross');
    const netTerminalValue = getTerminalValue(calculationDetail, 'net');
    const yielSaleCurrentNIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalNetOperatingIncome?.at(
            -1
        ) *
            12) /
        grossTerminalValue;

    const yielSaleCurrentGIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalGrossRentalIncome?.at(
            -1
        ) *
            12) /
        netTerminalValue;

    const yielSalePotentialNIY =
        ((calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalPotentialRent?.at(
            -1
        ) -
            (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalNonRecs?.at(
                -1
            ) +
                calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.otherIncomeCosts_beforeNOI?.at(
                    -1
                ))) *
            12) /
        grossTerminalValue;

    const yielSalePotentialGIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalPotentialRent?.at(
            -1
        ) *
            12) /
        netTerminalValue;

    const yielSaleMarketNIY =
        ((calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalMArketRent?.at(
            -1
        ) -
            (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalNonRecs?.at(
                -1
            ) +
                calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.otherIncomeCosts_beforeNOI?.at(
                    -1
                ))) *
            12) /
        grossTerminalValue;

    const yielSaleMarketGIY =
        (calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow?.totalMArketRent?.at(
            -1
        ) *
            12) /
        netTerminalValue;

    return { yielSaleMarketGIY, yielSaleMarketNIY, yielSalePotentialGIY, yielSalePotentialNIY, yielSaleCurrentGIY, yielSaleCurrentNIY }
}

export const getWALTYears = (calculationDetail) => {
    const rentRolls =
        calculationDetail?.rentRolls && Object.values(calculationDetail?.rentRolls);
    const totalGrossRentalIncome =
        calculationDetail?.assetDCFResult?.dcfResult?.assetCashFlow
            ?.totalGrossRentalIncome;
    const grossRentalIncomeTenant =
        calculationDetail?.assetDCFResult?.dcfResult?.tenantCashFlows[0]
            ?.grossRentalIncome;

    let waltRationPerRentalUnit;

    if (
        calculationDetail?.rentRolls &&
        dayjs(rentRolls[0]?.leaseEndDate).diff(
            dayjs(calculationDetail?.analysisDate),
            "days"
        ) > 0
    ) {
        const divisionResults = grossRentalIncomeTenant
            ?.map(
                (value, index) => (value / totalGrossRentalIncome[index]) * 2 // will replace with leaseEnd Date
            )
            ?.filter((num) => !isNaN(num));
        waltRationPerRentalUnit = divisionResults?.length > 0 && divisionResults?.reduce(
            (total, item) => total + item,
            0
        );
    } else {
        const divisionResults = grossRentalIncomeTenant
            ?.map(
                (value, index) => (value / totalGrossRentalIncome[index]) * 2 // will replace with analysis Date
            )
            ?.filter((num) => !isNaN(num));
        waltRationPerRentalUnit = divisionResults?.length > 0 && divisionResults?.reduce(
            (total, item) => total + item,
            0
        );
    }

    let waltYears =
        (waltRationPerRentalUnit -
            calculationDetail?.assetHVLParams?.leaseDuration) /
        36525;
    return waltYears;
}